import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Button,
    Spinner,
    useToast,
    Box,
    Heading,
    Input,
    InputGroup,
    Flex,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";

const Requests = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useToast();
    const tableRef = useRef(null);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState(""); // Add this state
    const [filteredRequests, setFilteredRequests] = useState([]); // Add this state

    const UUID = localStorage.getItem("UUID");
    const HuserID = localStorage.getItem("HuserID");

    const API_BASE_URL = "https://qaapirgeneng.digitalfields.co.za/ws1.cfc";

    const fetchRequests = useCallback(
        async (searchKey = "") => {
            try {
                setLoading(true);
                let url = `${API_BASE_URL}?method=listRequests&huserID=${HuserID}&uuid=${UUID}`;

                // Add search parameter if provided
                if (searchKey && searchKey.length >= 3) {
                    url += `&searchKey=${encodeURIComponent(searchKey)}`;
                }

                const response = await fetch(url, {
                    method: "POST",
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                setRequests(data);
                setFilteredRequests(data);
            } catch (error) {
                console.error("Error fetching request list:", error);
                toast({
                    title: "Error fetching request list",
                    description: "",
                    status: "error",
                    duration: 6000,
                    isClosable: true,
                });
            } finally {
                setLoading(false);
            }
        },
        [HuserID, UUID, toast]
    );

    // Update search handler to call API instead of client-side filtering
    const handleSearch = () => {
        if (searchTerm.length < 3) {
            toast({
                title: "Search term too short",
                description: "Please enter at least 3 characters to search",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        // Call API with search term
        fetchRequests(searchTerm);
    };

    // Update clear search to fetch all requests
    const handleClearSearch = () => {
        setSearchTerm("");
        fetchRequests(""); // Fetch all requests without search
    };

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
            >
                <Spinner size="xl" />
            </Box>
        );
    }

    // Navigate to Edit Request page
    const handleEditRequest = (e) => {
        const grandParentEl = e.target.parentElement.parentElement;
        navigate(`/editrequest/${grandParentEl.getAttribute("data-id")}`, {
            state: { scrollPosition: location.state?.scrollPosition },
        });
    };

    return (
        <Box p={5}>
            <Heading mb={5}>Request List</Heading>
            <Flex align="center" mb={5}>
                <Flex gap={2} alignItems="center">
                    <InputGroup width="300px">
                        <Input 
                            placeholder="Search requests (min. 3 chars)" 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleSearch();
                                }
                            }}
                        />
                    </InputGroup>
                    <Button
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        w="40px"
                        p={0}
                        onClick={handleSearch}
                    >
                        <SearchIcon />
                    </Button>
                    {searchTerm && (
                        <Button
                            size="sm"
                            onClick={handleClearSearch}
                        >
                            Clear
                        </Button>
                    )}
                </Flex>
            </Flex>
            <Box ref={tableRef} overflowY="auto" maxHeight="70vh">
                <Table variant="striped">
                    <Thead>
                        <Tr>
                            <Th>Candidate</Th>
                            <Th>Client</Th>
                            <Th>Status</Th>
                            <Th>DateTime Recorded</Th>
                            <Th></Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {filteredRequests.map((request) => ( // Use filteredRequests instead of requests
                            <Tr
                                key={request.HrequestID}
                                data-id={request.HrequestID}
                            >
                                <Td>{request.candidate}</Td>
                                <Td>{request.clientText}</Td>
                                <Td>{request.statusText}</Td>
                                <Td>{request.recordedDateTime}</Td>
                                <Td>
                                    <Button
                                        variant="outline-rounded"
                                        onClick={handleEditRequest}
                                    >
                                        Manage
                                    </Button>
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>
        </Box>
    );
};

export default Requests;
